// src/portfolio/data.js — content + file tree + themes.
// Edit identity, projects, and copy here. No JSX in this file.

// ── Themes ───────────────────────────────────────────────────────────
export const THEMES = {
  dark: {
    bg:        '#0e1014',
    surface:   '#16181d',
    surface2:  '#1c1f26',
    chrome:    '#0a0b0e',
    border:    '#23272f',
    borderHi:  '#2e3340',
    fg:        '#e6e6e6',
    fgStrong:  '#ffffff',
    mute:      '#9aa0ac',
    dim:       '#5a6070',
    accent:    '#c2756e',
    sx: {
      key:    '#bb9af7',
      str:    '#9ece6a',
      num:    '#ff9e64',
      fn:     '#7aa2f7',
      type:   '#2ac3de',
      cmt:    '#565f89',
      punct:  '#a9b1d6',
      head:   '#f7768e',
      link:   '#73daca',
      tag:    '#f7768e',
    },
    trafficGreen: '#27c93f',
    trafficYellow: '#ffbd2e',
    trafficRed: '#ff5f56',
    selection: 'rgba(122,162,247,0.22)',
  },
  light: {
    bg:        '#fbfaf6',
    surface:   '#f3f0e8',
    surface2:  '#ece8db',
    chrome:    '#ece8db',
    border:    '#dfdac8',
    borderHi:  '#cdc6b0',
    fg:        '#26241f',
    fgStrong:  '#0d0c0a',
    mute:      '#797362',
    dim:       '#a8a59b',
    accent:    '#b3573e',
    sx: {
      key:    '#7c4dff',
      str:    '#3b7d4e',
      num:    '#9a5a14',
      fn:     '#2752a3',
      type:   '#187a8c',
      cmt:    '#a1957a',
      punct:  '#5a554a',
      head:   '#b3573e',
      link:   '#2c8a78',
      tag:    '#b3573e',
    },
    trafficGreen: '#cdc6b0',
    trafficYellow: '#cdc6b0',
    trafficRed: '#cdc6b0',
    selection: 'rgba(124,77,255,0.18)',
  },
};

// ── File tree ──────────────────────────────────────────────────────
export const FILES = [
  { kind: 'folder', name: 'about', open: true, children: [
    { kind: 'md',   id: 'about',  name: 'README.md' },
    { kind: 'json', id: 'whoami', name: 'whoami.json' },
  ]},
  { kind: 'folder', name: 'projects', open: true, children: [
    { kind: 'md', id: 'cibc',     name: 'cibc-aml-platform.md',      badge: 'CIBC' },
    { kind: 'md', id: 'refactor', name: 'onprem-to-delta-refactor.md', badge: '~3× fewer' },
    { kind: 'md', id: 'citco',    name: 'citco-jdbc-ingestion.md',   badge: 'Citco' },
    { kind: 'md', id: 'jarvis',   name: 'jarvis-platform.md',        badge: 'Jarvis' },
  ]},
  { kind: 'folder', name: 'personal', open: true, children: [
    { kind: 'md', id: 'azure-pipeline', name: 'azure-data-pipeline.md', badge: 'Azure' },
    { kind: 'md', id: 'weather-stream', name: 'realtime-weather.md',    badge: 'Streaming' },
  ]},
  { kind: 'folder', name: 'credentials', open: false, children: [
    { kind: 'md', id: 'certifications', name: 'certifications.md' },
    { kind: 'md', id: 'education',      name: 'education.md' },
  ]},
  { kind: 'folder', name: 'experience', open: false, children: [
    { kind: 'tsv', id: 'experience', name: 'experience.tsv' },
  ]},
  { kind: 'pdf',  id: 'resume',  name: 'resume.pdf' },
  { kind: 'json', id: 'contact', name: 'contact.json' },
];

function flattenFiles(nodes, parent = '', out = []) {
  for (const n of nodes) {
    if (n.kind === 'folder') flattenFiles(n.children, (parent ? parent + '/' : '') + n.name, out);
    else out.push({ ...n, path: (parent ? parent + '/' : '') + n.name });
  }
  return out;
}
export const FILE_INDEX = flattenFiles(FILES);
export const FILE_BY_ID = Object.fromEntries(FILE_INDEX.map(f => [f.id, f]));

// Used by Resume.jsx (via id.name, id.role, etc.). Doc strings are hardcoded
// rather than templated — the renderer does not substitute these placeholders.
export const IDENTITY = {
  name: 'Visagan Shan',
  role: 'Data Engineer',
  location: 'Toronto, ON',
  email: 'visagan.shan@gmail.com',
  github: 'VisaganS',
  linkedin: 'visagan-shan',
};

export const COMMANDS = [
  { id: 'cmd.toggle-theme',    label: 'Toggle theme (dark / light)', kbd: '⌘ T', kind: 'cmd', icon: '◐' },
  { id: 'cmd.toggle-minimap',  label: 'Toggle minimap',              kbd: '',    kind: 'cmd', icon: '▦' },
  { id: 'cmd.toggle-linenum',  label: 'Toggle line numbers',         kbd: '',    kind: 'cmd', icon: '#' },
  { id: 'cmd.copy-email',      label: 'Copy email address',          kbd: '⌘ E', kind: 'cmd', icon: '✉' },
  { id: 'cmd.open-github',     label: 'Open GitHub profile',         kbd: '⌘ G', kind: 'cmd', icon: '↗' },
  { id: 'cmd.open-linkedin',   label: 'Open LinkedIn',               kbd: '⌘ L', kind: 'cmd', icon: '↗' },
  { id: 'cmd.download-resume', label: 'View résumé',                 kbd: '⌘ R', kind: 'cmd', icon: '↓' },
  { id: 'cmd.close-tab',       label: 'Close active tab',            kbd: '⌘ W', kind: 'cmd', icon: '×' },
  { id: 'cmd.close-all',       label: 'Close all tabs',              kbd: '',    kind: 'cmd', icon: '×' },
];

// ── Document content ────────────────────────────────────────────────
export const DOCS = {
  about: {
    title: 'README.md',
    lang: 'markdown',
    blocks: [
      { type: 'meta', items: ['~/about/', '·', 'last edited 2 days ago', '·', 'main'] },
      { type: 'h1', text: 'Visagan Shan' },
      { type: 'subtitle', text: 'Data Engineer · Toronto, ON · Databricks-certified' },
      { type: 'p', text: 'I\'m an early-career data engineer working on pipelines in regulated banking environments — Spark, Delta Lake, and Unity Catalog on Azure. I\'m lucky to be learning on real enterprise systems instead of toy projects, and I\'m enjoying every bit of it.' },
      { type: 'p', text: 'Most recently I contributed to view-based access control work and a regulatory reporting migration at a major Canadian bank. Before that, JDBC ingestion work on a global financial services platform. I\'m currently on placement at another Canadian bank working with the same Databricks-on-Azure stack.' },
      { type: 'callout', kind: 'tip', text: 'Press ⌘K to open the command palette — or click any file on the left.' },
      { type: 'h2', text: 'Selected work' },
      { type: 'fileref', items: [
        { id: 'cibc',     label: 'CIBC — AML data platform',       hint: '· VBAC + regulatory migration' },
        { id: 'refactor', label: 'On-prem → Delta refactor',       hint: '· 2,000+ Spark jobs → ~680' },
        { id: 'citco',    label: 'Citco — JDBC ingestion module',  hint: '· 4 sources → Databricks CDP' },
        { id: 'jarvis',   label: 'Jarvis — internal platform',      hint: '· medallion ELT + masking' },
      ]},
      { type: 'h2', text: 'Personal projects' },
      { type: 'fileref', items: [
        { id: 'azure-pipeline', label: 'Azure data pipeline',         hint: '· SQL Server → ADF → Databricks → Synapse → Power BI' },
        { id: 'weather-stream', label: 'Real-time weather reporting', hint: '· Event Hub + Databricks + Fabric' },
      ]},
      { type: 'h2', text: 'Stack' },
      { type: 'chips', items: ['Spark','Delta Lake','Databricks','Unity Catalog','Kafka','Azure Data Factory','Synapse','ADLS Gen2','AWS','GCP','Python','SQL','Scala','Java','Jenkins','Docker','Git','Bitbucket'] },
      { type: 'h2', text: 'Elsewhere' },
      { type: 'fileref', items: [
        { id: 'resume',         label: 'resume.pdf',        hint: '· one page' },
        { id: 'certifications', label: 'certifications.md', hint: '· what I\'ve studied for' },
        { id: 'contact',        label: 'contact.json',      hint: '· say hi' },
      ]},
    ],
  },

  whoami: {
    title: 'whoami.json',
    lang: 'json',
    blocks: [
      { type: 'code', lang: 'json', text:
`{
  %s%"name"%/%: %s%"Visagan Shan"%/%,
  %s%"role"%/%: %s%"Data Engineer"%/%,
  %s%"based_in"%/%: %s%"Toronto, ON"%/%,
  %s%"focus"%/%: %s%"Databricks, Spark, Delta Lake, Unity Catalog"%/%,
  %s%"certifications"%/%: [
    %s%"Databricks Data Engineer Professional"%/%,
    %s%"Databricks Data Engineer Associate"%/%
  ],
  %s%"currently_learning"%/%: [
    %s%"streaming patterns with DLT"%/%,
    %s%"data governance in regulated environments"%/%,
    %s%"writing better tests for Spark jobs"%/%
  ],
  %s%"open_to"%/%: [
    %s%"full-time data engineering roles"%/%,
    %s%"contract placements"%/%
  ]
}`
      },
    ],
  },

  cibc: {
    title: 'cibc-aml-platform.md',
    lang: 'markdown',
    blocks: [
      { type: 'meta', items: ['~/projects/cibc-aml-platform', '·', 'Dec 2025 — Mar 2026', '·', 'CIBC · AML Data Platform'] },
      { type: 'h1', text: 'CIBC — AML data platform' },
      { type: 'subtitle', text: 'Contributed to view-based access control work across 10+ data-product schemas and a regulatory reporting migration through SIT, UAT1, UAT2, and PROD.' },
      { type: 'kv', rows: [
        ['Role',   'Data Engineer (consultant via Jarvis)'],
        ['Domain', 'Anti-Money Laundering · regulatory reporting'],
        ['Scope',  '10+ data-product schemas · 4 parallel environments'],
        ['Stack',  'Databricks on Azure · Spark · Delta Lake · Unity Catalog'],
      ]},
      { type: 'h2', text: 'What I worked on' },
      { type: 'ul', items: [
        '**View-based access control (VBAC) work** across 10+ data-product schemas — wrote view + column specifications, helped manage deployment pipelines, and supported rollouts across SIT, UAT1, UAT2, and PROD.',
        '**Regulatory transaction reporting migration** — built Spark ETL pipelines to ingest, transform, and promote source data through staging into gold-layer Delta tables.',
        '**Schema alignment** — helped define DDLs and align schemas across workstreams so downstream regulatory consumers saw a consistent contract.',
        '**Environment governance tasks** — AD security group access grants, change-management tickets for promotion workflows, table/schema provisioning to support QA validation cycles.',
      ]},
      { type: 'callout', kind: 'note', text: 'The trickiest part wasn\'t the pipelines themselves — it was keeping four environments in sync while many engineers were shipping to them at the same time.' },
      { type: 'h2', text: 'What I took away from it' },
      { type: 'p', text: 'AML data has a lot of stakeholders — regulators, internal analysts, model builders — and each one needs a different slice of the same tables. Seeing how VBAC layered on top of Unity Catalog to handle that, and how change-management tickets fit into the deployment loop, was a crash course in how governance actually works in a regulated environment.' },
    ],
  },

  refactor: {
    title: 'onprem-to-delta-refactor.md',
    lang: 'markdown',
    blocks: [
      { type: 'meta', items: ['~/projects/onprem-to-delta-refactor', '·', 'CIBC · 2026', '·', '~3× fewer Spark jobs per run'] },
      { type: 'h1', text: 'On-prem → Delta refactor' },
      { type: 'subtitle', text: 'Took an on-prem AML reporting module — daily counts of flagged transactions across data products — and re-implemented it to run on Unity Catalog. Cut Spark job count from 2,000+ to roughly 680 per run along the way.' },
      { type: 'kv', rows: [
        ['What it does',  'Daily counts of flagged-transaction tables across data products'],
        ['Before',        'On-prem reporting module · 2,000+ Spark jobs / run'],
        ['After',         'Unity Catalog Delta tables · ~680 Spark jobs / run'],
        ['Why it exists', 'AML requires daily visibility into flagged-record volumes'],
      ]},
      { type: 'h2', text: 'Context' },
      { type: 'p', text: 'Our AML platform flags transaction records across multiple data products and teams. To meet regulatory and operational expectations, the team needs a daily view of how many records were flagged, and how much data we processed to find them — table counts, broken down by data product, every day.' },
      { type: 'p', text: 'The original reporting module ran on-prem, reading from on-prem tables. As part of the broader platform move, the underlying tables had already migrated to Databricks Unity Catalog as Delta tables. The on-prem reporting module was the last piece still pointing at the old world.' },
      { type: 'h2', text: 'What I worked on' },
      { type: 'p', text: 'My piece was re-implementing that reporting module so it could be scheduled and executed inside Unity Catalog, reading directly from the Delta tables. Functionally a port — same daily output, same downstream consumers — but a full rewrite of the code that produces it.' },
      { type: 'h2', text: 'The Spark job count problem' },
      { type: 'p', text: 'Once the rewrite was running, a single daily run was spawning over 2,000 Spark jobs. The original module had been written one table at a time — a separate read + count operation for each flagged-transaction table, repeated across every data product. Direct-porting that shape to Spark meant each per-table count became its own job, and the runtime graph ballooned.' },
      { type: 'h2', text: 'The shape of the fix' },
      { type: 'ul', items: [
        '**Group by data product.** Instead of one job per table, counts for the same data product were processed together so Spark could plan the work as a single stage.',
        '**Drive from metadata.** A single config describes the tables and data products to scan, so the job structure is shaped by configuration, not by how the original code happened to be laid out.',
        '**Read Delta directly.** Reading Unity Catalog Delta tables in-place removed an entire class of round-trips that the on-prem code path was still implicitly making.',
      ]},
      { type: 'code', lang: 'scala', text:
`%c%// Before: one Spark job per table, 2,000+ jobs per run%/%
%k%val%/% counts = reportingTables.%f%map%/% { table =>
  %k%val%/% df = spark.%f%read%/%.%f%table%/%(table)
  (table, df.%f%count%/%())   %c%// one job each%/%
}

%c%// After: grouped by data product, ~680 jobs per run%/%
%k%import%/% com.aml.reporting.{ReportSpec, runDailyCounts}

%k%val%/% specs: %t%Seq%/%[%t%ReportSpec%/%] = configLoader.%f%load%/%(%s%"reporting.yml"%/%)
%k%val%/% results = %f%runDailyCounts%/%(spark, specs)

results.%f%write%/%.%f%format%/%(%s%"delta"%/%)
  .%f%mode%/%(%s%"overwrite"%/%)
  .%f%saveAsTable%/%(%s%"gold.aml_daily_counts"%/%)`
      },
      { type: 'h2', text: 'Outcome' },
      { type: 'ul', items: [
        'Spark job graph shrank from 2,000+ to roughly 680 per run — still meaningful work, but much smaller and much more predictable.',
        'The reporting module now lives entirely on Unity Catalog and runs on a schedule, with the rest of the platform.',
        'Onboarding a new data product is a config change, not a code change — the next team that adds flagged-transaction tables doesn\'t have to touch the module.',
      ]},
      { type: 'callout', kind: 'note', text: 'The lesson I took from this: porting code shape from one runtime to another usually leaves performance on the floor. Once I stopped thinking of it as a port and started thinking of it as a Spark job, the right grouping became obvious.' },
    ],
  },

  citco: {
    title: 'citco-jdbc-ingestion.md',
    lang: 'markdown',
    blocks: [
      { type: 'meta', items: ['~/projects/citco-jdbc-ingestion', '·', 'Jul 2025 — Oct 2025', '·', 'Citco · Data Migration'] },
      { type: 'h1', text: 'Citco — JDBC ingestion module' },
      { type: 'subtitle', text: 'Worked on the JDBC ingestion module inside Citco\'s Centralized Data Platform — four source databases, one Databricks workspace, an active-semi-active DR posture.' },
      { type: 'kv', rows: [
        ['Sources',  'MySQL · Postgres · SQL Server · Oracle'],
        ['Target',   'Databricks Unified Workspace (CDP)'],
        ['Pipeline', 'Jenkins + Bitbucket for LOB onboarding'],
        ['DR model', 'Active-semi-active · DLT / managed-table replication'],
      ]},
      { type: 'h2', text: 'What I worked on' },
      { type: 'ul', items: [
        '**JDBC ingestion module** development and enhancement inside Citco\'s CDP — helping enable integration from four source databases into Databricks.',
        '**Automated pipelines** with Jenkins + Bitbucket for Line-of-Business (LOB) onboarding to the CDP Databricks Unified Workspace.',
        '**Integration + unit testing frameworks** — wrote tests so module behaviour was checkable in CI, not just by feel.',
        '**Disaster recovery** — contributed to the active-semi-active replication strategy for Delta Live Tables and managed tables.',
      ]},
      { type: 'h2', text: 'Why it mattered' },
      { type: 'p', text: 'Citco\'s LOB teams kept their data wherever the application happened to live — MySQL here, Oracle there, SQL Server somewhere else. The ingestion module turned "we have data in N places" into "we have data in one place, and the path from N to one is in version control."' },
      { type: 'callout', kind: 'note', text: 'The DR piece was new territory for me. It was the first time I saw, end to end, how a reporting platform actually plans to survive a region outage.' },
    ],
  },

  jarvis: {
    title: 'jarvis-platform.md',
    lang: 'markdown',
    blocks: [
      { type: 'meta', items: ['~/projects/jarvis-platform', '·', 'Apr 2024 — present', '·', 'Jarvis Consulting Group · Internal Platform'] },
      { type: 'h1', text: 'Jarvis — internal data platform' },
      { type: 'subtitle', text: 'Where I get to learn the patterns I later see on client placements: medallion ELT in Unity Catalog, YAML-driven ingestion, a data-masking framework for PII/PCI.' },
      { type: 'kv', rows: [
        ['Architecture', 'Bronze / Silver / Gold in Unity Catalog'],
        ['Ingestion',    'Salesforce · AWS · Snowflake (YAML-configured)'],
        ['Governance',   'Parameterized views + UDF-based masking'],
        ['Stack',        'Databricks · Delta Lake · Spark · Unity Catalog'],
      ]},
      { type: 'h2', text: 'What I worked on' },
      { type: 'ul', items: [
        '**End-to-end ELT pipelines** across Bronze, Silver, and Gold layers in Databricks Unity Catalog — Delta Lake throughout for auditable pipelines.',
        '**Automated ingestion** for Salesforce, AWS, and Snowflake driven by YAML configs. New sources can onboard by writing a config rather than a new pipeline.',
        '**Data-masking framework** in Unity Catalog using parameterized views and UDF-based masking functions, designed around the shape of PII / PCI data.',
        '**RBAC vs. dynamic masking benchmarking** — measured the query / latency overhead of each approach and used those numbers to tune Spark configs.',
        '**Documentation** — helped capture ingestion patterns, Spark tuning notes, and workspace governance docs the team reuses.',
      ]},
      { type: 'callout', kind: 'tip', text: 'The internal platform has been my main classroom. Trying things here under controlled load is how I get ready for the regulated environments where you can\'t iterate in production.' },
      { type: 'h2', text: 'Why YAML-driven ingestion' },
      { type: 'p', text: 'Onboarding a new data source is a common ask. If it\'s a code change, it takes a couple of weeks. If it\'s a config change, it can be done in an afternoon. The platform is shaped around making the second one possible.' },
    ],
  },

  // ── Personal projects ───────────────────────────────────────────
  // Each project may end with a `youtube` block:
  //   { type: 'youtube', id: 'dQw4w9WgXcQ', caption: 'Demo · 2 min' }
  // The `id` accepts a bare 11-character YouTube ID or a full URL.

  'azure-pipeline': {
    title: 'azure-data-pipeline.md',
    lang: 'markdown',
    blocks: [
      { type: 'meta', items: ['~/personal/azure-data-pipeline', '·', '2024', '·', 'personal'] },
      { type: 'h1', text: 'Azure data pipeline' },
      { type: 'subtitle', text: 'An end-to-end cloud migration: on-prem SQL Server → Azure Data Lake → Databricks → Synapse → Power BI.' },
      { type: 'kv', rows: [
        ['Source',         'Microsoft SQL Server (on-prem)'],
        ['Ingestion',      'Azure Data Factory'],
        ['Storage',        'Azure Data Lake Gen2'],
        ['Transformation', 'Databricks (Spark + Scala)'],
        ['Serving',        'Azure Synapse Analytics'],
        ['Visualization',  'Power BI'],
      ]},
      { type: 'h2', text: 'The problem' },
      { type: 'p', text: 'A lot of businesses sit on data that\'s spread across multiple sources, hard to consolidate, and slow to move through traditional ETL. By the time the numbers reach a dashboard, the decision window is already closing. I wanted to build a small, end-to-end version of the cloud-native pattern that addresses this — something I could point at and say "this is how the pieces fit together."' },
      { type: 'h2', text: 'What I built' },
      { type: 'ul', items: [
        '**Ingestion with Azure Data Factory** — pulled data from an on-prem SQL Server into Azure Data Lake Gen2 on a scheduled cadence.',
        '**Transformation in Databricks** — cleaned and reshaped the data using Spark with Scala notebooks, landing it in a structured form ready for serving.',
        '**Serving with Synapse Analytics** — loaded the processed data into Synapse for fast, SQL-style querying.',
        '**Visualization in Power BI** — built a dashboard connected to Synapse so changes flow through to the visuals without manual refreshes.',
      ]},
      { type: 'h2', text: 'What I learned' },
      { type: 'ul', items: [
        'How the Azure data stack pieces actually plug into each other — ADF, ADLS, Databricks, and Synapse each do one thing well, and the seams are where most of the work lives.',
        'Why landing zone + transform layer + serving layer is a useful mental model even at small scale.',
        'How much faster iteration gets when ingestion and visualization aren\'t coupled — changing the dashboard doesn\'t mean touching the pipeline.',
      ]},
      { type: 'h2', text: 'Demo' },
      { type: 'youtube', id: 'https://www.loom.com/share/1487a5854e1c4e308a881ac79640f413', caption: 'Walkthrough · Loom · ~5 min' },
    ],
  },

  'weather-stream': {
    title: 'realtime-weather.md',
    lang: 'markdown',
    blocks: [
      { type: 'meta', items: ['~/personal/realtime-weather', '·', '2025', '·', 'personal'] },
      { type: 'h1', text: 'Real-time weather reporting' },
      { type: 'subtitle', text: 'A streaming pipeline that ingests live weather data, processes it in real time, and alerts on extreme conditions.' },
      { type: 'kv', rows: [
        ['Source',     'Weather API'],
        ['Ingestion',  'Azure Functions + Event Hub'],
        ['Processing', 'Azure Databricks (Structured Streaming)'],
        ['Storage',    'Kusto DB (Azure Data Explorer)'],
        ['Insights',   'Microsoft Fabric'],
        ['Output',     'Power BI dashboard + Data Activator alerts'],
      ]},
      { type: 'h2', text: 'The idea' },
      { type: 'p', text: 'Batch pipelines are most of what I work with day-to-day, so I wanted a side project that forced me into the streaming side — something where the data actually moves, where latency matters, and where the alerting question is real instead of theoretical. Weather data is a nice fit: free, continuous, and with obvious "is this an extreme value?" thresholds to build alerts around.' },
      { type: 'h2', text: 'What I built' },
      { type: 'ul', items: [
        '**Real-time ingestion** — Azure Functions hit a weather API on a schedule and push readings into Event Hub.',
        '**Streaming processing in Databricks** — a Structured Streaming job reads from Event Hub, cleans and reshapes the readings, and writes them onward.',
        '**Optimized storage in Kusto DB** — readings land in Kusto for fast time-series querying.',
        '**Microsoft Fabric for real-time insights** — wires the storage layer through to dashboards and alerts.',
        '**Power BI dashboard** — interactive view of current conditions and trends.',
        '**Automated alerts with Data Activator** — triggers when readings cross extreme-weather thresholds.',
      ]},
      { type: 'h2', text: 'What I learned' },
      { type: 'ul', items: [
        'Streaming costs add up faster than batch — making intentional cost-vs-latency trade-offs is part of the design, not an afterthought.',
        'Event Hub + Databricks Structured Streaming is a surprisingly approachable starting point for real-time pipelines.',
        'Kusto Query Language (KQL) is genuinely nice for time-series data — a different shape of thinking from SQL.',
        'Alerts are easy to set up and hard to set up *well* — thresholds need tuning so they\'re actionable, not noise.',
      ]},
    ],
  },

  certifications: {
    title: 'certifications.md',
    lang: 'markdown',
    blocks: [
      { type: 'meta', items: ['~/credentials/certifications.md'] },
      { type: 'h1', text: 'Certifications' },
      { type: 'subtitle', text: 'Studied for, sat, and passed.' },
      { type: 'kv', rows: [
        ['Databricks Data Engineer Professional', '2025 · Databricks'],
        ['Databricks Data Engineer Associate',    '2025 · Databricks'],
        ['Python for Data Science, AI & Dev',     '2024 · IBM'],
        ['Intro to Cloud Computing',              '2024'],
      ]},
      { type: 'h2', text: 'Why these' },
      { type: 'p', text: 'The Databricks track lines up with the work — Unity Catalog, Delta Live Tables, and Spark internals are what I touch most days. The IBM and cloud-computing ones are foundations, kept because foundations are what everything else sits on.' },
    ],
  },

  education: {
    title: 'education.md',
    lang: 'markdown',
    blocks: [
      { type: 'meta', items: ['~/credentials/education.md'] },
      { type: 'h1', text: 'Education' },
      { type: 'h2', text: 'Seneca Polytechnic College' },
      { type: 'kv', rows: [
        ['Program', 'Diploma in Computer Programming'],
        ['Faculty', 'Applied Science & Engineering Technology'],
        ['Years',   '2019 — 2023'],
        ['Result',  'Graduated with honours · GPA 3.7 / 4.0'],
      ]},
      { type: 'h2', text: 'BrainStation' },
      { type: 'kv', rows: [
        ['Program', 'Certificate in Software Engineering'],
        ['Year',    '2023'],
        ['Result',  'Graduated with distinction · 92% cumulative'],
      ]},
    ],
  },

  experience: {
    title: 'experience.tsv',
    lang: 'tsv',
    blocks: [
      { type: 'table', headers: ['period', 'role', 'company', 'stack'], rows: [
        ['Dec 2025 — Mar 2026', 'Data Engineer', 'CIBC · AML Data Platform', 'Databricks · Spark · Delta · Unity Catalog · Azure'],
        ['Jul 2025 — Oct 2025', 'Data Engineer', 'Citco · Data Migration',   'Databricks · JDBC · Jenkins · Bitbucket · DLT'],
        ['Apr 2024 — present',  'Data Engineer', 'Jarvis Consulting Group',  'Databricks · Delta Lake · Unity Catalog · Salesforce · AWS · Snowflake'],
      ]},
      { type: 'callout', kind: 'tip', text: 'See ~/projects/ for write-ups of each placement.' },
    ],
  },

  resume: {
    title: 'resume.pdf',
    lang: 'pdf',
    blocks: [{ type: 'pdf' }],
  },

  contact: {
    title: 'contact.json',
    lang: 'json',
    blocks: [
      { type: 'code', lang: 'json', text:
`{
  %c%// Easiest first.%/%
  %s%"email"%/%:    %s%"visagan.shan@gmail.com"%/%,
  %s%"linkedin"%/%: %s%"linkedin.com/in/visagan-shan"%/%,
  %s%"github"%/%:   %s%"github.com/VisaganS"%/%,

  %s%"based_in"%/%:    %s%"Toronto, ON"%/%,
  %s%"responds_in"%/%: %s%"~24h, weekdays"%/%,

  %s%"happy_to_chat_about"%/%: [
    %s%"Databricks certifications"%/%,
    %s%"starting out in data engineering"%/%,
    %s%"life as a consultant at a Jarvis-style firm"%/%,
    %s%"that 2,000-job Spark refactor (now ~680)"%/%
  ]
}`
      },
      { type: 'callout', kind: 'tip', text: 'Press ⌘K → "Copy email" to grab the address quickly. ⌘L jumps to LinkedIn, ⌘G to GitHub.' },
      { type: 'p', text: 'I\'m especially happy to hear from people earlier in their data engineering careers — happy to compare notes on Databricks, certs, or starting out in consulting.' },
    ],
  },
};